import React, { useState, useEffect } from "react";
import axios from "axios";
import { Mail, Phone, MapPin, Briefcase, Award, Calendar, Search, Filter, X, Plus, User, Building, Globe } from "lucide-react";
import CandidateFormComprehensive from "../components/CandidateFormComprehensive";
import { useToast } from "../components/shared/ToastProvider";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Candidates = () => {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterSource, setFilterSource] = useState("all");
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const { showToast } = useToast();

  useEffect(() => {
    fetchCandidates();
  }, []);

  const fetchCandidates = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/candidates`);
      setCandidates(response.data);
    } catch (error) {
      console.error("Error fetching candidates:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredCandidates = candidates.filter(candidate => {
    const fullName = candidate.name || `${candidate.first_name || ''} ${candidate.last_name || ''}`.trim();
    const matchesSearch = fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         candidate.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (candidate.current_title && candidate.current_title.toLowerCase().includes(searchTerm.toLowerCase())) ||
                         (candidate.first_name && candidate.first_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
                         (candidate.last_name && candidate.last_name.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesSource = filterSource === "all" || candidate.source === filterSource;
    return matchesSearch && matchesSource;
  });

  const sourceColors = {
    manual: "bg-blue-100 text-blue-800",
    linkedin: "bg-indigo-100 text-indigo-800",
    indeed: "bg-green-100 text-green-800",
    monster: "bg-purple-100 text-purple-800"
  };

  return (
    <div className="space-y-6" data-testid="candidates-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">All Candidates</h1>
          <p className="text-gray-600 mt-1">Manage and view all candidate profiles</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          <span>Add Candidate</span>
        </button>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-xl shadow-md p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, email, or title..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              data-testid="candidate-search-input"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="h-5 w-5 text-gray-400" />
            <select
              value={filterSource}
              onChange={(e) => setFilterSource(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              data-testid="source-filter"
            >
              <option value="all">All Sources</option>
              <option value="manual">Manual</option>
              <option value="linkedin">LinkedIn</option>
              <option value="indeed">Indeed</option>
              <option value="monster">Monster</option>
            </select>
          </div>
        </div>
        <div className="mt-3 text-sm text-gray-600">
          Showing {filteredCandidates.length} of {candidates.length} candidates
        </div>
      </div>

      {/* Candidates List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : filteredCandidates.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <Briefcase className="h-16 w-16 mx-auto text-gray-400 mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            {candidates.length === 0 ? "No candidates yet" : "No matching candidates"}
          </h3>
          <p className="text-gray-600">
            {candidates.length === 0 
              ? "Start sourcing candidates or add them manually"
              : "Try adjusting your search or filters"}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCandidates.map((candidate) => (
            <div
              key={candidate.id}
              onClick={() => setSelectedCandidate(candidate)}
              className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer card-hover"
              data-testid={`candidate-card-${candidate.id}`}
            >
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-gray-900">
                    {candidate.name || `${candidate.first_name || ''} ${candidate.last_name || ''}`.trim() || 'Unnamed Candidate'}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">{candidate.current_title || 'N/A'}</p>
                  {candidate.current_company && (
                    <p className="text-xs text-gray-500">{candidate.current_company}</p>
                  )}
                  {candidate.candidate_priority && candidate.candidate_priority !== 'medium' && (
                    <span className={`inline-block mt-1 px-2 py-1 rounded-full text-xs font-medium ${
                      candidate.candidate_priority === 'high' ? 'bg-orange-100 text-orange-800' :
                      candidate.candidate_priority === 'hot' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {candidate.candidate_priority.toUpperCase()}
                    </span>
                  )}
                </div>
                <div className="text-right">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${sourceColors[candidate.source] || 'bg-gray-100 text-gray-800'}`}>
                    {candidate.source}
                  </span>
                  {candidate.candidate_id && (
                    <p className="text-xs text-gray-500 mt-1">ID: {candidate.candidate_id}</p>
                  )}
                </div>
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex items-center text-sm text-gray-600">
                  <Mail className="h-4 w-4 mr-2 text-gray-400" />
                  <span className="truncate">{candidate.email}</span>
                </div>
                {(candidate.phone || candidate.mobile_phone) && (
                  <div className="flex items-center text-sm text-gray-600">
                    <Phone className="h-4 w-4 mr-2 text-gray-400" />
                    <span>{candidate.phone || candidate.mobile_phone}</span>
                  </div>
                )}
                <div className="flex items-center text-sm text-gray-600">
                  <MapPin className="h-4 w-4 mr-2 text-gray-400" />
                  <span>{candidate.location}</span>
                </div>
                {candidate.total_experience_years > 0 && (
                  <div className="flex items-center text-sm text-gray-600">
                    <Briefcase className="h-4 w-4 mr-2 text-gray-400" />
                    <span>{candidate.total_experience_years} years experience</span>
                  </div>
                )}
                {candidate.job_search_status && (
                  <div className="flex items-center text-sm text-gray-600">
                    <User className="h-4 w-4 mr-2 text-gray-400" />
                    <span className="capitalize">{candidate.job_search_status.replace('_', ' ')}</span>
                  </div>
                )}
                {candidate.remote_work_preference && (
                  <div className="flex items-center text-sm text-gray-600">
                    <Globe className="h-4 w-4 mr-2 text-gray-400" />
                    <span className="capitalize">{candidate.remote_work_preference.replace('_', ' ')}</span>
                  </div>
                )}
              </div>

              {candidate.skills && candidate.skills.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-gray-600 mb-2">Skills:</p>
                  <div className="flex flex-wrap gap-1">
                    {candidate.skills.slice(0, 4).map((skill, idx) => (
                      <span
                        key={idx}
                        className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-md"
                      >
                        {skill}
                      </span>
                    ))}
                    {candidate.skills.length > 4 && (
                      <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-md">
                        +{candidate.skills.length - 4}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Candidate Detail Modal */}
      {selectedCandidate && (
        <CandidateDetailModal
          candidate={selectedCandidate}
          onClose={() => setSelectedCandidate(null)}
        />
      )}

      {showCreateModal && (
        <CandidateFormComprehensive
          candidate={null}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            fetchCandidates();
            toast.success('Candidate added successfully!');
          }}
        />
      )}
    </div>
  );
};

const CandidateDetailModal = ({ candidate, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-testid="candidate-detail-modal">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900">Candidate Profile</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-2xl font-bold text-gray-900">{candidate.name}</h3>
              <p className="text-gray-600 mt-1">{candidate.current_title} {candidate.current_company && `at ${candidate.current_company}`}</p>
            </div>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
              candidate.source === 'manual' ? 'bg-blue-100 text-blue-800' :
              candidate.source === 'linkedin' ? 'bg-indigo-100 text-indigo-800' :
              candidate.source === 'indeed' ? 'bg-green-100 text-green-800' :
              'bg-purple-100 text-purple-800'
            }`}>
              {candidate.source}
            </span>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center space-x-2 text-gray-700">
              <Mail className="h-4 w-4 text-gray-400" />
              <span className="text-sm">{candidate.email}</span>
            </div>
            {(candidate.phone || candidate.mobile_phone) && (
              <div className="flex items-center space-x-2 text-gray-700">
                <Phone className="h-4 w-4 text-gray-400" />
                <span className="text-sm">{candidate.phone || candidate.mobile_phone}</span>
              </div>
            )}
            <div className="flex items-center space-x-2 text-gray-700">
              <MapPin className="h-4 w-4 text-gray-400" />
              <span className="text-sm">{candidate.location}</span>
            </div>
            {candidate.total_experience_years > 0 && (
              <div className="flex items-center space-x-2 text-gray-700">
                <Briefcase className="h-4 w-4 text-gray-400" />
                <span className="text-sm">{candidate.total_experience_years} years experience</span>
              </div>
            )}
          </div>

          {candidate.summary && (
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Summary</h4>
              <p className="text-gray-700">{candidate.summary}</p>
            </div>
          )}

          {candidate.skills && candidate.skills.length > 0 && (
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Skills</h4>
              <div className="flex flex-wrap gap-2">
                {candidate.skills.map((skill, idx) => (
                  <span key={idx} className="px-3 py-1 bg-purple-100 text-purple-700 text-sm rounded-full">
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          )}

          {candidate.work_experience && candidate.work_experience.length > 0 && (
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">Work Experience</h4>
              <div className="space-y-3">
                {candidate.work_experience.map((exp, idx) => (
                  <div key={idx} className="border-l-4 border-blue-500 pl-4">
                    <h5 className="font-semibold text-gray-900">{exp.title}</h5>
                    <p className="text-sm text-gray-600">{exp.company}</p>
                    <p className="text-xs text-gray-500">{exp.start_date} - {exp.end_date || 'Present'}</p>
                    {exp.description && <p className="text-sm text-gray-700 mt-1">{exp.description}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {candidate.education && candidate.education.length > 0 && (
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">Education</h4>
              <div className="space-y-2">
                {candidate.education.map((edu, idx) => (
                  <div key={idx} className="flex items-start space-x-3">
                    <Award className="h-5 w-5 text-blue-500 mt-1" />
                    <div>
                      <p className="font-medium text-gray-900">{edu.degree} in {edu.field_of_study}</p>
                      <p className="text-sm text-gray-600">{edu.institution}</p>
                      {edu.graduation_year && <p className="text-xs text-gray-500">Graduated {edu.graduation_year}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {candidate.certifications && candidate.certifications.length > 0 && (
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Certifications</h4>
              <ul className="list-disc list-inside space-y-1">
                {candidate.certifications.map((cert, idx) => (
                  <li key={idx} className="text-sm text-gray-700">{cert}</li>
                ))}
              </ul>
            </div>
          )}

          {candidate.linkedin_url && (
            <div>
              <a
                href={candidate.linkedin_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                View LinkedIn Profile →
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Candidates;
